import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'auth-sso-kit/client/index.js';
import { Check, ExternalLink, Plus } from 'lucide-react';
import { api, ApiError } from '../api/client';
import { naviguer } from '../App';
import { useRole } from '../context/RoleContext';
import { PersonneSelect } from '../components/demande/PersonneSelect';
import { MiniCalendrier, iso } from '../components/demande/MiniCalendrier';
import { useMaPersonne } from '../components/demande/useMaPersonne';
import { useMeta } from '../components/demande/useMeta';
import { StatutBadge } from '../components/demande/StatutBadge';
import { PoleDot } from '../components/demande/PoleDot';
import { BANNIERE_TIPEE, formatDateCourt, lienTipee } from '../components/demande/format';
import { queNom } from '../components/demande/nouvelle/textesDemande';
import type { Absence, Atelier, ControleSolde, Demande, DoublonDemande, HistoriqueRemplacement, Personne, SeanceProgramme } from '../types';

// ANCIENNE page « Nouvelle demande » (assistant en 3 étapes) : gardée le temps de la validation de la nouvelle (calendrier + « Ma demande », DECISIONS §26),
// accessible par #nouvelle?version=ancienne. À supprimer une fois la nouvelle page validée sur dev.
//
// Assistant multi-étapes, miroir de l'intake BPMN : absent·e + dates/ateliers →
// remplaçant déjà trouvé ? → notes/récap. Une demande est créée par
// (atelier × date) — l'API accepte un lot en une seule requête
// (api.createDemandes). La sélection des dates et des ateliers impactés se
// fait dans un même écran (calendrier + liste), pour éviter un aller-retour.
//
// On est dans l'app remplacement : toute demande présuppose qu'un·e
// remplaçant·e est nécessaire (besoin_remplacant = true). Le cas « absence
// sans remplaçant » n'est pas demandé ici — c'est une décision back-office
// prise depuis la fiche (transition valider_absence_simple → absence_validee,
// RH/responsable), pour ne pas alourdir la saisie du cas courant.
//
// Un seul mécanisme de sélection (clic = jour, glisser = plage) couvre les
// trois cas d'usage (un atelier ponctuel / plusieurs / une période) : cliquer
// et glisser produisent le même ensemble de dates, donc pas de bascule de
// mode à choisir en amont. Le champ Du/Au reste disponible, replié, pour les
// absences longues où saisir au clavier est plus rapide que glisser.
type Etape = 1 | 2 | 3;

function etapeSuivante(e: Etape): Etape {
  return e === 1 ? 2 : 3;
}
function etapePrecedente(e: Etape): Etape {
  return e === 3 ? 2 : 1;
}

/** Toutes les dates ISO entre du et au (inclus). */
function joursDansPlage(du: string, au: string): string[] {
  if (!du || !au) return du ? [du] : [];
  const debut = new Date(du + 'T00:00:00');
  const fin = new Date(au + 'T00:00:00');
  if (debut > fin) return [du];
  const jours: string[] = [];
  for (let cur = debut; cur <= fin; cur = new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1)) {
    jours.push(iso(cur));
  }
  return jours;
}

/** Samedi/dimanche exclus — aucun atelier n'a jamais lieu ce jour-là (cf.
 *  MiniCalendrier, qui ne les affiche même plus) : les garder dans une plage
 *  ferait apparaître, plus bas dans la liste par date, un jour sans aucun
 *  atelier possible et qu'on n'a même pas pu choisir sur le calendrier. */
function estJourOuvre(dateIso: string): boolean {
  const jour = new Date(dateIso + 'T00:00:00').getDay();
  return jour !== 0 && jour !== 6;
}

function initiales(prenom: string, nom: string): string {
  return `${prenom[0] ?? ''}${nom[0] ?? ''}`.toUpperCase();
}

/** Clé (atelier × date) — même format partout où on doit reconnaître LA MÊME
 *  séance : résolution (étape 2), présélection/badge « déjà demandé »
 *  (étape 1), et récapitulatif (étape 3). */
function cleSeance(atelierId: number, date: string): string {
  return `${atelierId}_${date}`;
}

interface NouvelleDemandePageProps {
  /** Pré-remplissage depuis un lien profond (ex. clic sur un trou de couverture
   *  du Calendrier) : #nouvelle?absent=15&date=2026-07-20. */
  prefill?: { absent?: string; date?: string };
}

export function NouvelleDemandeAncienne({ prefill }: NouvelleDemandePageProps = {}) {
  const { user } = useAuth();
  const { isAdmin, aRole } = useRole();
  const { meta } = useMeta();
  const [etape, setEtape] = useState<Etape>(1);

  // Étape 1 — absent·e. Les rôles de gestion (et l'admin) choisissent librement
  // l'intervenant·e, sans présélection. Un·e intervenant·e simple ne peut créer
  // une demande que pour sa propre absence : champ verrouillé sur sa fiche
  // (même règle appliquée côté serveur — le client n'est qu'une commodité UX).
  const peutChoisirAutrui = isAdmin || aRole('responsable', 'secretariat', 'rh', 'planification');
  const [absentId, setAbsentId] = useState<number | null>(() => {
    const id = prefill?.absent ? Number(prefill.absent) : NaN;
    return Number.isNaN(id) ? null : id;
  });

  const [personnesActives, setPersonnesActives] = useState<Personne[]>([]);
  useEffect(() => { api.getPersonnes({ actif: true }).then(setPersonnesActives).catch(() => {}); }, []);

  const maPersonne = useMaPersonne(personnesActives);

  useEffect(() => {
    if (peutChoisirAutrui || absentId !== null || !maPersonne) return;
    setAbsentId(maPersonne.id);
  }, [peutChoisirAutrui, maPersonne, absentId]);

  // Étape 1 — quand ? (sélection unique par clic/glisser + ajout par plage Du/Au)
  const [moisAffiche, setMoisAffiche] = useState(() => prefill?.date ? new Date(prefill.date + 'T00:00:00') : new Date());
  const [datesSelectionnees, setDatesSelectionnees] = useState<Set<string>>(
    // Un lien profond peut pointer une date déjà passée (favori, lien partagé
    // ancien) — on ne la préremplit pas, comme pour toute autre sélection.
    () => new Set(prefill?.date && prefill.date >= iso(new Date()) ? [prefill.date] : []),
  );
  const [periodeOuverte, setPeriodeOuverte] = useState(false);
  const [periode, setPeriode] = useState<{ du: string; au: string }>({ du: '', au: '' });

  const datesValides = useMemo(() => Array.from(datesSelectionnees).sort(), [datesSelectionnees]);

  function onJourClick(dateStr: string) {
    if (dateStr < iso(new Date())) return; // défense en profondeur, en plus du calendrier
    setDatesSelectionnees((prev) => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr); else next.add(dateStr);
      return next;
    });
  }

  // Glissement souris/tactile sur le calendrier : ajoute toute la plage a→b d'un coup.
  function onRangeSelect(a: string, b: string) {
    const [du, au] = a <= b ? [a, b] : [b, a];
    const aujourd = iso(new Date());
    setDatesSelectionnees((prev) => new Set([...prev, ...joursDansPlage(du, au).filter((d) => d >= aujourd && estJourOuvre(d))]));
  }

  function ajouterPeriode() {
    if (!periode.du || !periode.au) return;
    // Filet côté logique : le calendrier bloque déjà les jours passés, mais la
    // saisie clavier Du/Au pourrait en produire — on ne garde que ≥ aujourd'hui.
    const aujourd = iso(new Date());
    setDatesSelectionnees((prev) => new Set([...prev, ...joursDansPlage(periode.du, periode.au).filter((d) => d >= aujourd && estJourOuvre(d))]));
    setPeriode({ du: '', au: '' });
  }

  function effacerSelection() {
    setDatesSelectionnees(new Set());
    setPeriode({ du: '', au: '' });
    setDatesRetouchees(new Set());
    setSaisieLibre({});
    setAteliersLibres({});
  }

  const aSelection = datesValides.length > 0;

  // Ateliers (référentiel complet, pour la recherche manuelle et les titulaires).
  const [ateliersMap, setAteliersMap] = useState<Record<number, Atelier>>({});
  useEffect(() => {
    api.getAteliers({ actif: true }).then((liste) => {
      setAteliersMap(Object.fromEntries(liste.map((a) => [a.id, a])));
    }).catch(() => {});
  }, []);

  // Séances des mois affichés (pour les points-repère sur le calendrier) —
  // le calendrier montre désormais mois affiché + suivant, donc la plage
  // couvre les deux (voir MiniCalendrier : glisser peut franchir la frontière).
  const [seancesMois, setSeancesMois] = useState<SeanceProgramme[]>([]);
  useEffect(() => {
    const du = iso(new Date(moisAffiche.getFullYear(), moisAffiche.getMonth(), 1));
    const au = iso(new Date(moisAffiche.getFullYear(), moisAffiche.getMonth() + 2, 0));
    let annule = false;
    api.getSeances({ du, au }).then((s) => { if (!annule) setSeancesMois(s); }).catch(() => {});
    return () => { annule = true; };
  }, [moisAffiche]);

  const joursMarques = useMemo(() => {
    if (absentId == null) return new Set<string>();
    const dates = new Set<string>();
    for (const s of seancesMois) {
      if (ateliersMap[s.atelier_id]?.titulaire_personne_id === absentId) dates.add(s.date);
    }
    return dates;
  }, [seancesMois, ateliersMap, absentId]);

  // Séances couvrant les dates choisies (un seul appel pour toute la plage).
  const [seancesParDate, setSeancesParDate] = useState<Record<string, SeanceProgramme[]>>({});
  const [chargementSeances, setChargementSeances] = useState(false);
  const [erreurSeances, setErreurSeances] = useState<string | null>(null);
  const [selections, setSelections] = useState<Record<string, Set<number>>>({});
  // Demandes déjà actives de l'absent·e sur la même plage — pour ne pas
  // ré-cocher automatiquement (ni laisser croire qu'il faut re-demander) un
  // atelier déjà en recherche. Chargées avec les séances (même effet) plutôt
  // que dans un effet séparé : sinon une résolution plus lente de l'une des
  // deux crée une fenêtre où la présélection tourne sans connaître l'autre.
  const [demandesExistantes, setDemandesExistantes] = useState<Demande[]>([]);
  const demandeExistanteParCle = useMemo(() => {
    const m = new Map<string, Demande>();
    for (const d of demandesExistantes) m.set(cleSeance(d.atelier_id, d.date_atelier), d);
    return m;
  }, [demandesExistantes]);
  // Dates où l'utilisateur a coché/décoché un atelier à la main — la
  // présélection automatique ne doit plus jamais y toucher (voir plus bas).
  const [datesRetouchees, setDatesRetouchees] = useState<Set<string>>(new Set());
  // Saisie de texte libre par date, pour ce qui n'est PAS un atelier du
  // référentiel (réunion, rendez-vous, formation…).
  const [saisieLibre, setSaisieLibre] = useState<Record<string, string>>({});
  // Éléments hors atelier saisis en texte libre, par date : pas de fiche
  // atelier à rattacher, donc consignés dans les notes de la demande à la
  // création plutôt que modélisés comme un vrai atelier sélectionné.
  const [ateliersLibres, setAteliersLibres] = useState<Record<string, string[]>>({});

  function ajouterAtelierLibre(date: string, nom: string) {
    const trimmed = nom.trim();
    if (!trimmed) return;
    setAteliersLibres((prev) => ({ ...prev, [date]: [...(prev[date] ?? []), trimmed] }));
    setSaisieLibre((prev) => ({ ...prev, [date]: '' }));
  }

  function retirerAtelierLibre(date: string, index: number) {
    setAteliersLibres((prev) => ({ ...prev, [date]: (prev[date] ?? []).filter((_, i) => i !== index) }));
  }

  // Changer d'absent·e (vue gestion) : les retouches manuelles, la saisie
  // libre et les notes hors atelier de la personne précédente n'ont plus de
  // sens pour la nouvelle — sans ce reset, datesRetouchees empêcherait le
  // recalcul de la présélection pour la nouvelle personne, et le texte libre
  // de l'ancienne se retrouverait dans les notes de la nouvelle demande.
  useEffect(() => {
    setDatesRetouchees(new Set());
    setSaisieLibre({});
    setAteliersLibres({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [absentId]);

  useEffect(() => {
    if (datesValides.length === 0) return;
    const du = datesValides[0];
    const au = datesValides[datesValides.length - 1];
    let annule = false;
    setChargementSeances(true);
    setErreurSeances(null);
    Promise.all([
      api.getSeances({ du, au }),
      absentId != null ? api.getDemandes({ personne_id: absentId, du, au, actives: true }) : Promise.resolve<Demande[]>([]),
    ])
      .then(([toutes, demandesRes]) => {
        if (annule) return;
        const parDate: Record<string, SeanceProgramme[]> = {};
        for (const s of toutes) {
          if (!datesSelectionnees.has(s.date)) continue;
          (parDate[s.date] ??= []).push(s);
        }
        setSeancesParDate(parDate);
        setDemandesExistantes(demandesRes);
        const dejaDemandee = new Set(demandesRes.map((d) => cleSeance(d.atelier_id, d.date_atelier)));
        // Présélectionne les ateliers dont l'absent·e est titulaire. Si la
        // personne est choisie APRÈS les dates (courant en vue gestion), les
        // dates déjà chargées ont d'abord reçu une présélection vide (aucun
        // titulaire pour absentId=null) — on la recalcule dès que absentId
        // change, sauf si l'utilisateur a lui-même coché/décoché sur la date.
        // Un atelier déjà couvert par une demande active n'est PAS
        // présélectionné (mais reste cochable si l'utilisateur y tient — un
        // badge « déjà demandé » l'indique, voir le rendu de l'étape 1).
        setSelections((prev) => {
          const next = { ...prev };
          for (const d of datesValides) {
            if (d in next && datesRetouchees.has(d)) continue;
            const titulaireIds = (parDate[d] ?? [])
              .filter((s) => ateliersMap[s.atelier_id]?.titulaire_personne_id === absentId)
              .filter((s) => !dejaDemandee.has(cleSeance(s.atelier_id, d)))
              .map((s) => s.atelier_id);
            next[d] = new Set(titulaireIds);
          }
          return next;
        });
      })
      .catch((err: unknown) => { if (!annule) setErreurSeances(err instanceof Error ? err.message : 'Impossible de charger les séances.'); })
      .finally(() => { if (!annule) setChargementSeances(false); });
    return () => { annule = true; };
    // ateliersMap est nécessaire : sans lui, si les dates/la personne sont
    // choisies avant la fin du chargement du référentiel des ateliers, le
    // calcul de présélection tourne avec un référentiel vide et ne se
    // relance jamais (la liste affichée se corrige au rendu suivant, mais
    // pas les cases cochées, calculées une fois pour toutes ci-dessus).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesValides.join(','), absentId, ateliersMap]);

  const ateliersChoisis = useMemo(
    () => Object.entries(selections)
      .filter(([date]) => datesSelectionnees.has(date))
      .flatMap(([date, set]) => Array.from(set).map((atelier_id) => ({ atelier_id, date_atelier: date }))),
    [selections, datesSelectionnees],
  );

  // Dates réellement concernées par un remplacement (sous-ensemble de
  // datesValides : une plage peut couvrir des jours sans aucun atelier de
  // l'absent·e — on ne les affiche/envoie pas comme "dates").
  const datesAvecAteliers = useMemo(
    () => Array.from(new Set(ateliersChoisis.map((a) => a.date_atelier))).sort(),
    [ateliersChoisis],
  );

  // Un rendez-vous/réunion en texte libre n'est pas un atelier à remplacer :
  // il ne peut pas, à lui seul, créer une demande (atelier_id est obligatoire
  // en base). On le garde à part pour affiner le message quand aucun atelier
  // n'est coché nulle part — dire pourquoi plutôt qu'une erreur générique.
  // Filtré par datesSelectionnees : désélectionner une date sur le calendrier
  // doit aussi faire disparaître le texte libre qu'elle portait, pas le
  // laisser traîner et réapparaître dans les notes d'une demande sans rapport.
  const datesAvecTexteLibre = useMemo(
    () => Object.entries(ateliersLibres).filter(([date, noms]) => noms.length > 0 && datesSelectionnees.has(date)).map(([date]) => date),
    [ateliersLibres, datesSelectionnees],
  );

  // ── Classement PAR séance (atelier × date) : disponibilité (absence Tipee à
  // CETTE date) d'abord, puis expérience sur CET atelier / son pôle. Purement
  // informatif, jamais un filtre (BPMN : n'importe qui peut être garant·e).
  const atelierIdsChoisis = useMemo(
    () => Array.from(new Set(ateliersChoisis.map((a) => a.atelier_id))).sort((a, b) => a - b),
    [ateliersChoisis],
  );

  // Historique de remplacement PAR atelier (un appel par atelier distinct).
  const [histParAtelier, setHistParAtelier] = useState<Record<number, Record<number, HistoriqueRemplacement>>>({});
  useEffect(() => {
    if (atelierIdsChoisis.length === 0) { setHistParAtelier({}); return; }
    let annule = false;
    Promise.all(atelierIdsChoisis.map((aid) => api.getHistoriqueRemplacements([aid]).then((h) => [aid, h] as const)))
      .then((entries) => {
        if (annule) return;
        const next: Record<number, Record<number, HistoriqueRemplacement>> = {};
        for (const [aid, list] of entries) next[aid] = Object.fromEntries(list.map((x) => [x.personne_id, x]));
        setHistParAtelier(next);
      })
      .catch(() => {});
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atelierIdsChoisis.join(',')]);

  // Absences (Tipee + manuelles) sur la plage → indisponibilités par date.
  const [absencesPeriode, setAbsencesPeriode] = useState<Absence[]>([]);
  useEffect(() => {
    if (datesAvecAteliers.length === 0) { setAbsencesPeriode([]); return; }
    let annule = false;
    api.getAbsences({ du: datesAvecAteliers[0], au: datesAvecAteliers[datesAvecAteliers.length - 1] })
      .then((a) => { if (!annule) setAbsencesPeriode(a); }).catch(() => {});
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datesAvecAteliers.join(',')]);
  const absentsParDate = useMemo(() => {
    const m = new Map<string, Set<number>>();
    for (const a of absencesPeriode) {
      if (!m.has(a.date_absence)) m.set(a.date_absence, new Set());
      m.get(a.date_absence)!.add(a.personne_id);
    }
    return m;
  }, [absencesPeriode]);

  type NiveauExp = 'fort' | 'moyen' | 'faible' | 'aucun';
  interface CandidatSeance { p: Personne; dispo: boolean; hist?: HistoriqueRemplacement; niveau: NiveauExp; }
  const ORDRE_EXP: Record<NiveauExp, number> = { fort: 0, moyen: 1, faible: 2, aucun: 3 };

  /** Candidats classés pour UNE séance : dispo d'abord, puis expérience.
   *  Internes uniquement — cette liste alimente la recherche interne (Slack),
   *  et le vivier externe n'y est pas joignable. Il reste sollicitable plus
   *  tard, séparément, une fois la recherche interne épuisée (action
   *  « Passer à la recherche externe », par e-mail). Mélanger les deux ici
   *  laissait croire à tort qu'on pouvait « prioriser » un·e externe dès
   *  cette étape. */
  function classementSeance(atelierId: number, date: string): CandidatSeance[] {
    const hists = histParAtelier[atelierId] ?? {};
    const absents = absentsParDate.get(date) ?? new Set<number>();
    const pole = ateliersMap[atelierId]?.pole_id ?? null;
    const scored: CandidatSeance[] = personnesActives
      .filter((p) => p.id !== absentId && !p.is_externe)
      .map((p) => {
        const hist = hists[p.id];
        let niveau: NiveauExp = 'aucun';
        if (hist && hist.nb_atelier > 0) niveau = 'fort';
        else if (hist && hist.nb_pole > 0) niveau = 'moyen';
        else if (pole != null && p.pole_ids?.includes(pole)) niveau = 'faible';
        return { p, dispo: !absents.has(p.id), hist, niveau };
      });
    scored.sort((a, b) => {
      if (a.dispo !== b.dispo) return a.dispo ? -1 : 1;
      if (ORDRE_EXP[a.niveau] !== ORDRE_EXP[b.niveau]) return ORDRE_EXP[a.niveau] - ORDRE_EXP[b.niveau];
      if ((b.hist?.nb_atelier ?? 0) !== (a.hist?.nb_atelier ?? 0)) return (b.hist?.nb_atelier ?? 0) - (a.hist?.nb_atelier ?? 0);
      return a.p.display_name.localeCompare(b.p.display_name, 'fr');
    });
    return scored;
  }

  function texteExp(c: CandidatSeance): string {
    if (c.niveau === 'fort') {
      const dernier = c.hist?.dernier_atelier
        ? ` (dernier : ${new Intl.DateTimeFormat('fr-CH', { month: 'long', year: 'numeric' }).format(new Date(c.hist.dernier_atelier))})`
        : '';
      return `a remplacé cet atelier ${c.hist!.nb_atelier}×${dernier}`;
    }
    if (c.niveau === 'moyen') return `a remplacé dans ce pôle ${c.hist!.nb_pole}×`;
    if (c.niveau === 'faible') return 'même pôle';
    return '—';
  }

  // Jours sélectionnés mais sans atelier retenu — affichés en plus discret
  // sur le calendrier pour ne pas laisser croire qu'ils comptent.
  const joursSansAtelier = useMemo(() => {
    const set = new Set<string>();
    for (const d of datesSelectionnees) {
      if (!selections[d]?.size) set.add(d);
    }
    return set;
  }, [datesSelectionnees, selections]);

  function toggleAtelier(date: string, atelierId: number) {
    setSelections((prev) => {
      const set = new Set(prev[date] ?? []);
      if (set.has(atelierId)) set.delete(atelierId); else set.add(atelierId);
      return { ...prev, [date]: set };
    });
    setDatesRetouchees((prev) => new Set(prev).add(date));
  }

  // Étape 2 — résolution par séance : remplaçant·e trouvé·e OU recherche à lancer.
  // (Pas d'option « atelier maintenu » : être sur l'app remplacement, c'est
  // chercher un·e remplaçant·e ; le cas « absence sans remplaçant » se traite
  // en back-office depuis la fiche.)
  type ResolutionType = 'remplacant' | 'recherche';
  interface SeanceResolution { type: ResolutionType; remplacantId: number | null; stagiaire: boolean; garantId: number | null; suggeresIds: number[]; }
  const RESO_DEFAUT: SeanceResolution = { type: 'recherche', remplacantId: null, stagiaire: false, garantId: null, suggeresIds: [] };
  const [resolutions, setResolutions] = useState<Record<string, SeanceResolution>>({});
  const [voirPlus, setVoirPlus] = useState<Record<string, boolean>>({});
  const getReso = (cle: string): SeanceResolution => resolutions[cle] ?? RESO_DEFAUT;
  const setReso = (cle: string, patch: Partial<SeanceResolution>) =>
    setResolutions((prev) => ({ ...prev, [cle]: { ...(prev[cle] ?? RESO_DEFAUT), ...patch } }));

  // Étape 3 — notes / récap
  const [notes, setNotes] = useState('');
  const [datesFlexibles, setDatesFlexibles] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Demandes déjà existantes (atelier × date déjà demandé) renvoyées par le
  // serveur au lieu d'être ignorées silencieusement — voir demandes.ts.
  const [doublonsExistants, setDoublonsExistants] = useState<DoublonDemande[] | null>(null);
  // Confirmation après création — jamais un naviguer() direct vers la
  // première demande créée : quand plusieurs séances sont demandées d'un
  // coup, ça masquait silencieusement les autres. La modale récapitule tout
  // ce qui a été créé et laisse choisir où aller ensuite.
  const [demandesCreees, setDemandesCreees] = useState<Demande[] | null>(null);
  // Aperçu d'une demande déjà existante — modale plutôt que naviguer() : on ne
  // quitte pas l'assistant, on peut fermer et en vérifier une autre juste après.
  const [demandeApercu, setDemandeApercu] = useState<Demande | null>(null);

  // Rappel Tipee — purement déclaratif, jamais bloquant : la fiche demande
  // continuera de relancer tant que ce n'est pas confirmé (voir DemandePage).
  const [tipeeUrl, setTipeeUrl] = useState('');
  const [tipeeConfirme, setTipeeConfirme] = useState(false);
  // Motif déclaré : les vacances passent par le contrôle du solde Tipee.
  const [motif, setMotif] = useState<'vacances' | 'autre' | null>(null);
  const [soldeInfo, setSoldeInfo] = useState<ControleSolde | null>(null);
  // Solde insuffisant constaté à l'envoi : on demande « continuer quand même ? ».
  const [avertSolde, setAvertSolde] = useState<ControleSolde | null>(null);
  useEffect(() => { api.getSettings().then((s) => setTipeeUrl(lienTipee(s['tipee.instance_url']))).catch(() => {}); }, []);

  const sequence: Etape[] = [1, 2, 3];
  const position = sequence.indexOf(etape) + 1;

  function seanceValide(s: { atelier_id: number; date_atelier: string }): boolean {
    const r = getReso(cleSeance(s.atelier_id, s.date_atelier));
    if (r.type === 'remplacant') return r.remplacantId != null && (!r.stagiaire || r.garantId != null);
    return true; // maintenue / recherche : toujours valides
  }

  function peutAvancer(): boolean {
    switch (etape) {
      case 1: return absentId != null && datesValides.length > 0 && ateliersChoisis.length > 0;
      case 2: return ateliersChoisis.every(seanceValide);
      default: return true;
    }
  }

  function suivant() {
    if (!peutAvancer()) {
      // Cas précis et fréquent : rien coché nulle part, seulement du texte
      // libre — expliquer pourquoi plutôt qu'un message générique (un
      // rendez-vous/réunion n'est pas un atelier, donc pas de demande
      // possible sans au moins un atelier coché quelque part).
      const message = etape === 1 && ateliersChoisis.length === 0 && datesAvecTexteLibre.length > 0
        ? "Une réunion ou un rendez-vous seul ne crée pas de demande — cochez au moins un atelier sur une des dates (votre texte sera ajouté à ses notes)."
        : 'Merci de compléter les champs requis avant de continuer.';
      setErreur(message);
      return;
    }
    setErreur(null);
    setDoublonsExistants(null);
    setDemandeApercu(null);
    setEtape((e) => etapeSuivante(e));
  }
  function precedent() {
    setErreur(null);
    setDoublonsExistants(null);
    setDemandeApercu(null);
    setEtape((e) => etapePrecedente(e));
  }

  // Vacances : solde Tipee affiché dès le choix du motif (information, pas encore un blocage).
  const clesDates = datesAvecAteliers.join(',');
  useEffect(() => {
    setSoldeInfo(null);
    setAvertSolde(null);
    if (motif !== 'vacances' || absentId == null || datesAvecAteliers.length === 0) return;
    let annule = false;
    api.controleSolde(absentId, datesAvecAteliers).then((c) => { if (!annule) setSoldeInfo(c); }).catch(() => {});
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [motif, absentId, clesDates]);

  async function creerDemandes(horsSoldeConfirme = false) {
    if (absentId == null || ateliersChoisis.length === 0) { setErreur('Aucun atelier sélectionné.'); return; }
    if (motif == null) { setErreur("Indiquez le motif de l'absence."); return; }
    if (!peutAvancer()) { setErreur('Complétez la résolution de chaque séance.'); return; }
    setEnvoi(true);
    setErreur(null);
    setDoublonsExistants(null);
    try {
      const ateliers = ateliersChoisis.map((s) => {
        const r = getReso(cleSeance(s.atelier_id, s.date_atelier));
        if (r.type === 'remplacant') {
          return {
            atelier_id: s.atelier_id, date_atelier: s.date_atelier, resolution: 'remplacant' as const,
            remplacant: { personne_id: r.remplacantId!, stagiaire: r.stagiaire, garant_personne_id: r.stagiaire && r.garantId != null ? r.garantId : undefined },
          };
        }
        return { atelier_id: s.atelier_id, date_atelier: s.date_atelier, resolution: 'recherche' as const, collegues_suggeres_ids: r.suggeresIds };
      });
      // Ateliers hors référentiel saisis en texte libre : pas de fiche à
      // rattacher à une demande, donc consignés dans les notes plutôt que
      // silencieusement perdus. Filtré par datesSelectionnees (voir
      // datesAvecTexteLibre) : une date désélectionnée ne doit plus contribuer.
      const ateliersLibresTexte = Object.entries(ateliersLibres)
        .filter(([date, noms]) => noms.length > 0 && datesSelectionnees.has(date))
        .map(([date, noms]) => `${formatDateCourt(date)} : ${noms.join(', ')}`)
        .join(' · ');
      const notesCompletes = [
        notes.trim(),
        ateliersLibresTexte ? `Hors atelier (réunion / rendez-vous / formation) — ${ateliersLibresTexte}` : '',
      ].filter(Boolean).join('\n\n');
      const { creees, doublons } = await api.createDemandes({
        absent_personne_id: absentId,
        dates: datesAvecAteliers,
        ateliers,
        dates_flexibles: datesFlexibles,
        notes: notesCompletes || undefined,
        tipee_rappel_vu: tipeeConfirme,
        motif_absence: motif,
        hors_solde_confirme: horsSoldeConfirme,
      });
      setAvertSolde(null);
      if (creees.length > 0) {
        // Les deux listes sont montrées ensemble dans la modale de
        // confirmation : un résultat mixte (certaines séances créées,
        // d'autres déjà existantes) ne doit jamais faire disparaître les
        // doublons de l'écran — sinon le compte affiché (« 2 demandes
        // enregistrées ») contredit le récapitulatif précédent (« 5 ateliers
        // concernés »), alors que les 5 ont bel et bien une demande active.
        setDemandesCreees(creees);
        if (doublons.length > 0) setDoublonsExistants(doublons);
      } else if (doublons.length > 0) {
        // Rien de nouveau créé : toutes les séances demandées existaient déjà.
        // On informe plutôt que de laisser l'utilisateur·rice face à un
        // bouton qui semble n'avoir rien fait. Le récap reste modifiable :
        // pas de modale de confirmation « terminé », on n'a rien fait de neuf.
        setDoublonsExistants(doublons);
      } else {
        setErreur('La création de la demande a échoué.');
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.corps.code === 'hors_solde') {
        setAvertSolde(err.corps.controle as ControleSolde);
      } else {
        setErreur(err instanceof Error ? err.message : 'La création de la demande a échoué.');
      }
    } finally {
      setEnvoi(false);
    }
  }

  const tousAteliers = useMemo(
    () => Object.values(ateliersMap).sort((a, b) => a.nom.localeCompare(b.nom, 'fr')),
    [ateliersMap],
  );

  // Ton des messages : si l'absent·e est le compte connecté, on s'adresse
  // directement à l'utilisateur·rice (« vous ») ; sinon (gestion créant pour
  // un tiers) on nomme la personne. Voir feedback-ton-adresse-utilisateur.
  const personneAbsente = useMemo(
    () => personnesActives.find((p) => p.id === absentId) ?? null,
    [personnesActives, absentId],
  );
  const pourMoi = absentId != null && maPersonne != null && absentId === maPersonne.id;
  const prenomAbsent = personneAbsente?.prenom ?? 'cette personne';
  // « de Marc » mais « d'Alexia » (élision devant une voyelle ou un h muet).
  const dePrenom = /^[aeiouyhàâäéèêëîïôöùûüœ]/i.test(prenomAbsent) ? `d'${prenomAbsent}` : `de ${prenomAbsent}`;
  const aujourdIso = iso(new Date());

  // Récapitulatif étape 3 — groupé par ce qui va concrètement se passer à
  // l'envoi plutôt qu'un simple tableau plat (plus facile à vérifier).
  const seancesTriees = [...ateliersChoisis].sort((a, b) => a.date_atelier.localeCompare(b.date_atelier));
  const seancesTrouvees = seancesTriees.filter((s) => getReso(cleSeance(s.atelier_id, s.date_atelier)).type === 'remplacant');
  const seancesRecherche = seancesTriees.filter((s) => getReso(cleSeance(s.atelier_id, s.date_atelier)).type === 'recherche');
  const periodeLabel = datesAvecAteliers.length === 0 ? ''
    : datesAvecAteliers[0] === datesAvecAteliers[datesAvecAteliers.length - 1]
      ? formatDateCourt(datesAvecAteliers[0])
      : `${formatDateCourt(datesAvecAteliers[0])} au ${formatDateCourt(datesAvecAteliers[datesAvecAteliers.length - 1])}`;
  const notesHorsAtelier = Object.entries(ateliersLibres)
    .filter(([date, noms]) => noms.length > 0 && datesSelectionnees.has(date))
    .map(([date, noms]) => `${formatDateCourt(date)} : ${noms.join(', ')}`)
    .join(' · ');

  // Séances déjà couvertes par une demande active, sur une date sélectionnée,
  // mais PAS cochées (donc pas dans ateliersChoisis) — filet de sécurité du
  // récap : rappelle que ce qui a été décoché suite au badge « déjà demandé »
  // (étape 1) est bel et bien déjà pris en charge, sans compter dans le total
  // envoyé. Si l'utilisateur a coché quand même malgré le badge, la séance
  // suit le chemin normal (Recherche à lancer / Remplaçant·e trouvé·e) — pas
  // de double affichage.
  const dejaDemandeesNonSelectionnees = useMemo(() => {
    const choisiesSet = new Set(ateliersChoisis.map((a) => cleSeance(a.atelier_id, a.date_atelier)));
    return demandesExistantes
      .filter((d) => datesSelectionnees.has(d.date_atelier) && !choisiesSet.has(cleSeance(d.atelier_id, d.date_atelier)))
      .sort((a, b) => a.date_atelier.localeCompare(b.date_atelier));
  }, [demandesExistantes, datesSelectionnees, ateliersChoisis]);

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Nouvelle demande</h1>
          <p className="page-meta">Étape {position} / {sequence.length}</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: etape === 3 ? 560 : 860 }}>
        {erreur && (
          <div className="badge badge-err mb-16" style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>
            {erreur}
          </div>
        )}

        {doublonsExistants && !demandesCreees && (
          <div className="card mb-16" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
            <div className="text-ink-1 mb-8">
              {doublonsExistants.length > 1
                ? 'Ces demandes existent déjà :'
                : 'Cette demande existe déjà :'}
            </div>
            <div className="flex flex-col gap-4">
              {doublonsExistants.map((d) => 'acces' in d && d.acces === 'prise_en_charge' ? (
                // Demande que cette personne ne peut pas ouvrir (réservée à la gestion) : aucun aperçu, rien d'autre n'est dit.
                <div key={`${d.atelier_id}_${d.date_atelier}`} className="text-sm text-ink-2">
                  {formatDateCourt(d.date_atelier)} · {d.atelier_nom}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''} — déjà prise en charge par l'équipe gestion
                </div>
              ) : (
                <button
                  key={(d as Demande).id}
                  type="button"
                  className="btn btn-sm"
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setDemandeApercu(d as Demande)}
                >
                  {formatDateCourt(d.date_atelier)} · {d.atelier_nom}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''} — Aperçu
                </button>
              ))}
            </div>
          </div>
        )}

        {etape === 1 && (
          <>
            <h3 className="section-label mb-12">Qui est absent·e ?</h3>
            {peutChoisirAutrui ? (
              <PersonneSelect
                id="absent"
                label="Intervenant·e absent·e"
                value={absentId}
                onChange={setAbsentId}
                filtres={{ actif: true }}
                required
              />
            ) : maPersonne ? (
              <div className="form-group">
                <label className="form-label" htmlFor="absent">Intervenant·e absent·e *</label>
                <input id="absent" type="text" className="form-input" value={maPersonne.display_name} disabled />
                <div className="text-xs text-ink-3 mt-4">La demande porte sur votre propre absence.</div>
              </div>
            ) : (
              <div className="badge badge-warn mb-12" style={{ display: 'block', padding: '8px 12px', textTransform: 'none', letterSpacing: 'normal', fontSize: 'var(--t-13)' }}>
                Votre compte n'est relié à aucune fiche intervenant·e — contactez le secrétariat pour pouvoir créer une demande.
              </div>
            )}

            <h3 className="section-label mt-16 mb-4">Quand ?</h3>
            <p className="text-xs text-ink-3 mb-12">
              Cliquez un jour, ou glissez sur le calendrier pour sélectionner plusieurs jours.
            </p>

            {/* Calendrier en haut, liste des dates EN PLEINE LARGEUR en dessous
                (plutôt que côte à côte dans une colonne étroite) : les cartes
                par date ont besoin d'espace pour respirer. */}
            <div>
              <div>
                <MiniCalendrier
                  mois={moisAffiche}
                  onMoisChange={setMoisAffiche}
                  selectionnees={datesSelectionnees}
                  selectionneesVides={joursSansAtelier}
                  marquees={joursMarques}
                  onJourClick={onJourClick}
                  onRangeSelect={onRangeSelect}
                />
                <div className="flex items-center gap-8 mt-8">
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPeriodeOuverte((v) => !v)}>
                    {periodeOuverte ? '▾' : '▸'} Absence longue ? Saisir une période
                  </button>
                  {aSelection && (
                    <button type="button" className="btn btn-sm btn-ghost" onClick={effacerSelection}>Effacer</button>
                  )}
                </div>
                {periodeOuverte && (
                  <div className="flex gap-8 items-end flex-wrap mt-8" style={{ width: 280 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Du</label>
                      <input type="date" className="form-input" min={aujourdIso} value={periode.du}
                        onChange={(e) => setPeriode((p) => ({ ...p, du: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Au</label>
                      <input type="date" className="form-input" min={periode.du || aujourdIso} value={periode.au}
                        onChange={(e) => setPeriode((p) => ({ ...p, au: e.target.value }))} />
                    </div>
                    <button type="button" className="btn btn-sm btn-accent" onClick={ajouterPeriode} disabled={!periode.du || !periode.au}>
                      Ajouter
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-16">
                {absentId == null ? (
                  <div className="text-xs text-ink-3">Sélectionnez d'abord l'intervenant·e absent·e.</div>
                ) : datesValides.length === 0 ? (
                  <div className="text-xs text-ink-3">Cliquez une ou plusieurs dates sur le calendrier.</div>
                ) : chargementSeances ? (
                  <div className="flex justify-center p-16"><div className="spinner" /></div>
                ) : erreurSeances ? (
                  <div className="text-err text-sm">{erreurSeances}</div>
                ) : (
                  <div>
                    {datesValides.map((d) => {
                      const seances = (seancesParDate[d] ?? []).filter((s) => ateliersMap[s.atelier_id]?.titulaire_personne_id === absentId);
                      const seanceAtelierIds = new Set((seancesParDate[d] ?? []).map((s) => s.atelier_id));
                      // On ne propose QUE les ateliers animés par l'absent·e (jamais
                      // tout le catalogue) : ceux détectés ce jour-là (auto-cochés),
                      // plus ses éventuels autres ateliers sans séance détectée ce
                      // jour-là (horaire exceptionnel / détection incomplète).
                      const mesAteliersExtras = tousAteliers.filter((a) => a.titulaire_personne_id === absentId && !seanceAtelierIds.has(a.id));
                      const libres = ateliersLibres[d] ?? [];
                      const saisie = saisieLibre[d] ?? '';

                      return (
                        <div className="date-card" key={d}>
                          <div className="date-card-title">{formatDateCourt(d)}</div>

                          {seances.length > 0 ? (
                            <div className="seance-groupe-prevu">
                              <div className="seance-groupe-prevu-head">
                                <span className="seance-dot-check"><Check size={10} /></span>
                                {seances.length === 1
                                  ? (pourMoi
                                    ? 'Votre atelier ce jour-là — décochez-le si aucun·e remplaçant·e n\'est à trouver.'
                                    : `L'atelier ${dePrenom} ce jour-là — décochez-le si aucun·e remplaçant·e n'est à trouver.`)
                                  : (pourMoi
                                    ? 'Vos ateliers ce jour-là — décochez ceux sans remplaçant·e à trouver.'
                                    : `Ateliers ${dePrenom} ce jour-là — décochez ceux sans remplaçant·e à trouver.`)}
                              </div>
                              <div className="flex flex-col gap-4">
                                {seances.map((s) => {
                                  const existante = demandeExistanteParCle.get(cleSeance(s.atelier_id, d));
                                  return (
                                    <div key={s.atelier_id} className="flex items-center gap-8">
                                      <label className="flex items-center gap-8 text-sm" style={{ flex: 1, minWidth: 0 }}>
                                        <input
                                          type="checkbox"
                                          checked={selections[d]?.has(s.atelier_id) ?? false}
                                          onChange={() => toggleAtelier(d, s.atelier_id)}
                                        />
                                        <span>
                                          {s.atelier_nom}
                                          {s.heure_debut && <span className="text-ink-3" style={{ fontWeight: 400 }}> · {s.heure_debut.slice(0, 5)}{s.heure_fin ? `–${s.heure_fin.slice(0, 5)}` : ''}</span>}
                                          {s.lieu && <span className="text-ink-3" style={{ fontWeight: 400 }}> · {s.lieu}</span>}
                                        </span>
                                      </label>
                                      {existante && (
                                        <>
                                          <span className="badge badge-plain" style={{ flexShrink: 0 }}>Déjà demandé</span>
                                          <button type="button" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => setDemandeApercu(existante)}>Voir</button>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="text-xs text-ink-3 mb-8">
                              {pourMoi
                                ? "Aucun de vos ateliers habituels ce jour-là."
                                : `Aucun atelier habituel ${dePrenom} ce jour-là.`}
                            </div>
                          )}

                          {mesAteliersExtras.length > 0 && (
                            <div className="seance-groupe-exceptionnel">
                              <div className="seance-groupe-exceptionnel-head">
                                <span className="seance-dot-outline" />
                                {pourMoi
                                  ? 'Un autre de vos ateliers, exceptionnellement ce jour-là ?'
                                  : `Un autre atelier ${dePrenom}, exceptionnellement ce jour-là ?`}
                              </div>
                              <div className="flex flex-col gap-4">
                                {mesAteliersExtras.map((a) => {
                                  const existante = demandeExistanteParCle.get(cleSeance(a.id, d));
                                  return (
                                    <div key={a.id} className="flex items-center gap-8">
                                      <label className="flex items-center gap-8 text-sm" style={{ flex: 1, minWidth: 0 }}>
                                        <input
                                          type="checkbox"
                                          checked={selections[d]?.has(a.id) ?? false}
                                          onChange={() => toggleAtelier(d, a.id)}
                                        />
                                        {a.nom}
                                        {a.lieu && <span className="text-ink-3" style={{ fontWeight: 400 }}> · {a.lieu}</span>}
                                      </label>
                                      {existante && (
                                        <>
                                          <span className="badge badge-plain" style={{ flexShrink: 0 }}>Déjà demandé</span>
                                          <button type="button" className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => setDemandeApercu(existante)}>Voir</button>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          <div style={{ border: '1px solid var(--line)', borderRadius: 6, padding: '8px 12px' }}>
                            <div className="text-xs text-ink-3 mb-8" id={`hors-atelier-desc-${d}`}>Ce n'est pas un atelier ? Notez ici une réunion, un rendez-vous ou une formation. Ce texte est recopié dans les notes de la demande, donc dans le message envoyé à vos collègues, et à des personnes extérieures si l'équipe gestion élargit la recherche : n'y indiquez aucune information médicale ou personnelle.</div>
                            <div className="flex gap-8">
                              <input
                                type="text"
                                className="form-input"
                                aria-describedby={`hors-atelier-desc-${d}`}
                                placeholder="Ex. réunion d'équipe, formation premiers secours…"
                                value={saisie}
                                onChange={(e) => setSaisieLibre((prev) => ({ ...prev, [d]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterAtelierLibre(d, saisie); } }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm"
                                disabled={!saisie.trim()}
                                onClick={() => ajouterAtelierLibre(d, saisie)}
                              >
                                <Plus className="icon-sm" /> Ajouter
                              </button>
                            </div>
                            {libres.length > 0 && (
                              <div className="flex flex-col gap-4 mt-8">
                                {libres.map((nom, i) => (
                                  <div key={i} className="flex items-center gap-8 text-sm">
                                    <span className="badge badge-accent">Hors atelier</span>
                                    <span className="flex-1">{nom}</span>
                                    <button type="button" className="btn btn-icon btn-sm" onClick={() => retirerAtelierLibre(d, i)} aria-label="Retirer">✕</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="text-xs text-ink-3 mt-12">
              {datesAvecAteliers.length} date(s) · {ateliersChoisis.length} atelier(s) impacté(s)
              {datesAvecTexteLibre.length > 0 && ` · ${datesAvecTexteLibre.length} note(s) hors atelier`}
            </div>

            <label className="flex items-start gap-8 text-sm mt-12">
              <input type="checkbox" checked={datesFlexibles} onChange={(e) => setDatesFlexibles(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Dates flexibles
                <span className="text-ink-3">
                  {' '}{pourMoi
                    ? "(je peux décaler mes dates d'absence si aucun·e remplaçant·e n'est trouvé·e)"
                    : `(les dates d'absence ${dePrenom} peuvent être décalées si aucun·e remplaçant·e n'est trouvé·e)`}
                </span>
              </span>
            </label>
          </>
        )}

        {etape === 2 && (
          <>
            <h3 className="section-label mb-4">Remplacement, séance par séance</h3>
            <div className="card mb-12">
              <p className="text-sm text-ink-1" style={{ margin: 0 }}>
                {peutChoisirAutrui
                  ? "Pour chaque séance, indiquez si un·e remplaçant·e est déjà trouvé·e. Sinon, choisissez « À rechercher » : un message pour les collègues est préparé dès l'envoi de la demande."
                  : "Pour chaque séance, dites-nous si vous avez déjà trouvé quelqu'un. Sinon, choisissez « À rechercher » : un message pour vos collègues est préparé dès l'envoi de votre demande."}
                <br />
                <strong>
                  {peutChoisirAutrui
                    ? `${prenomAbsent === 'cette personne' ? 'La personne absente' : prenomAbsent} cherche en premier ; l'équipe gestion prend le relais si personne n'est trouvé·e.`
                    : "C'est à vous de chercher en premier. Si personne n'est trouvé, l'équipe gestion vous aidera."}
                </strong>
              </p>
            </div>
            {[...ateliersChoisis].sort((a, b) => a.date_atelier.localeCompare(b.date_atelier)).map((s) => {
              const cle = cleSeance(s.atelier_id, s.date_atelier);
              const r = getReso(cle);
              const at = ateliersMap[s.atelier_id];
              return (
                <div className="card mb-12" key={cle}>
                  <div className="font-medium text-ink-1 mb-8">
                    {formatDateCourt(s.date_atelier)} · {at?.nom ?? s.atelier_id}
                    {at?.heure_debut && <span className="text-ink-3"> · {at.heure_debut.slice(0, 5)}{at.heure_fin ? `–${at.heure_fin.slice(0, 5)}` : ''}</span>}
                    {at?.lieu && <span className="text-ink-3"> · {at.lieu}</span>}
                  </div>

                  <div className="tabs mb-12" role="tablist" aria-label="Résolution de la séance">
                    <div role="tab" tabIndex={0} aria-selected={r.type === 'recherche'} className={`tab ${r.type === 'recherche' ? 'active' : ''}`}
                      onClick={() => setReso(cle, { type: 'recherche' })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setReso(cle, { type: 'recherche' }); }
                        else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); setReso(cle, { type: 'remplacant' }); }
                      }}
                    >
                      À rechercher
                    </div>
                    <div role="tab" tabIndex={0} aria-selected={r.type === 'remplacant'} className={`tab ${r.type === 'remplacant' ? 'active' : ''}`}
                      onClick={() => setReso(cle, { type: 'remplacant' })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setReso(cle, { type: 'remplacant' }); }
                        else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); setReso(cle, { type: 'recherche' }); }
                      }}
                    >
                      Remplaçant·e trouvé·e
                    </div>
                  </div>

                  {r.type === 'remplacant' && (
                    <>
                      <PersonneSelect id={`remp-${cle}`} label="Remplaçant·e" value={r.remplacantId} onChange={(v) => setReso(cle, { remplacantId: v })} filtres={peutChoisirAutrui ? { actif: true } : { actif: true, externe: false }} required />
                      <label className="flex items-center gap-8 text-sm mb-8">
                        <input type="checkbox" checked={r.stagiaire} onChange={(e) => setReso(cle, { stagiaire: e.target.checked })} /> Stagiaire
                      </label>
                      {r.stagiaire && (
                        <PersonneSelect id={`gar-${cle}`} label="Garant·e du cadre" value={r.garantId} onChange={(v) => setReso(cle, { garantId: v })} filtres={peutChoisirAutrui ? { actif: true } : { actif: true, externe: false }} required />
                      )}
                      <div className="text-xs text-ink-3">Le/la remplaçant·e désigné·e recevra une notification et devra confirmer dans l'app (compte créé automatiquement si besoin).</div>
                    </>
                  )}

                  {r.type === 'recherche' && (() => {
                    const classement = classementSeance(s.atelier_id, s.date_atelier);
                    const montrerTout = voirPlus[cle] ?? false;
                    const liste = montrerTout ? classement : classement.slice(0, 5);
                    return (
                      <div>
                        <div className="text-base font-medium text-ink-2 mb-8">Suggestions de remplaçant·e·s pour cette séance — les personnes disponibles d'abord. Cochez celles à contacter en priorité.</div>
                        <div className="flex flex-col gap-8" style={{ maxHeight: 320, overflowY: 'auto' }}>
                          {liste.map((c) => {
                            const fort = c.niveau === 'fort';
                            return (
                              <label
                                key={c.p.id}
                                className="flex items-center gap-12"
                                style={{
                                  padding: '10px 12px', borderRadius: 'var(--r2)',
                                  border: `1px solid ${fort ? 'var(--accent)' : 'var(--line)'}`,
                                  background: fort ? 'var(--accent-dim)' : 'var(--bg-inset)',
                                  opacity: c.dispo ? 1 : 0.55,
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={r.suggeresIds.includes(c.p.id)}
                                  onChange={() => setReso(cle, { suggeresIds: r.suggeresIds.includes(c.p.id) ? r.suggeresIds.filter((id) => id !== c.p.id) : [...r.suggeresIds, c.p.id] })}
                                />
                                <span
                                  className="text-xs font-semi"
                                  style={{
                                    width: 28, height: 28, borderRadius: 'var(--r-full)', flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', background: fort ? 'var(--accent)' : 'var(--ink-3)',
                                  }}
                                >
                                  {initiales(c.p.prenom, c.p.nom)}
                                </span>
                                <span className="flex-1" style={{ minWidth: 0 }}>
                                  <div className="text-md font-semi text-ink-1">{c.p.display_name}</div>
                                  {c.niveau !== 'aucun' && (
                                    <div className={`text-base ${fort ? 'text-accent font-medium' : 'text-ink-2'}`}>{texteExp(c)}</div>
                                  )}
                                </span>
                                {!c.dispo && <span className="badge badge-warn" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>⚠ absent·e</span>}
                              </label>
                            );
                          })}
                        </div>
                        {classement.length > 5 && (
                          <button type="button" className="btn btn-sm btn-ghost mt-8" onClick={() => setVoirPlus((prev) => ({ ...prev, [cle]: !montrerTout }))}>
                            {montrerTout ? 'Voir moins' : `Voir les ${classement.length - 5} autres`}
                          </button>
                        )}
                        <div className="text-xs text-ink-3 mt-8">
                          La recherche interne (Slack) sera lancée à la création{r.suggeresIds.length > 0 ? `, avec ${r.suggeresIds.length} suggestion(s)` : ''}.
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </>
        )}

        {etape === 3 && (
          <>
            <h3 className="section-label mb-12">Notes et récapitulatif</h3>
            <div className="form-group">
              <label className="form-label" htmlFor="notes">Précisions pour vos collègues</label>
              <textarea id="notes" className="form-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} aria-describedby="notes-aide" />
              <div id="notes-aide" className="text-xs text-ink-2 mt-4">
                Ces précisions sont recopiées dans le message de recherche pour vos collègues (Slack) et, si l'équipe gestion élargit la recherche, des personnes extérieures. N'y indiquez aucune information médicale ou personnelle.
              </div>
            </div>

            <div className="card bg-inset">
              <div className="card-title mb-8">Récapitulatif — à vérifier avant l'envoi</div>

              <p className="text-md text-ink-1" style={{ margin: '0 0 12px' }}>
                {pourMoi ? 'Votre absence' : <>Absence {/^[aeiouyhàâäéèêëîïôöùûüœ]/i.test(personneAbsente?.display_name ?? prenomAbsent) ? "d'" : 'de '}<strong>{personneAbsente?.display_name ?? prenomAbsent}</strong></>}
                {periodeLabel && <> du <strong>{periodeLabel}</strong></>}
                {' '}· <strong>{ateliersChoisis.length} atelier{ateliersChoisis.length > 1 ? 's' : ''}</strong> concerné{ateliersChoisis.length > 1 ? 's' : ''}
                {datesFlexibles && <> · <span className="badge badge-accent" style={{ marginLeft: 4 }}>Dates flexibles</span></>}
              </p>

              {seancesTrouvees.length > 0 && (
                <div className="mb-12">
                  <div className="text-xs font-semi mb-4" style={{ color: 'var(--ok)' }}>● Remplaçant·e déjà trouvé·e ({seancesTrouvees.length})</div>
                  <div className="flex flex-col gap-4">
                    {seancesTrouvees.map((s) => {
                      const cle = cleSeance(s.atelier_id, s.date_atelier);
                      const r = getReso(cle);
                      const at = ateliersMap[s.atelier_id];
                      const remp = personnesActives.find((p) => p.id === r.remplacantId);
                      return (
                        <div key={cle} className="flex items-center gap-8 text-sm" style={{ background: 'var(--ok-dim)', borderRadius: 'var(--r2)', padding: '6px 10px' }}>
                          <span className="font-medium" style={{ flexShrink: 0 }}>{at?.nom ?? s.atelier_id}{at?.lieu && <span className="text-ink-3 font-normal"> · {at.lieu}</span>}</span>
                          <span className="text-ink-2" style={{ flex: 1, minWidth: 0 }}>→ {remp?.display_name ?? '—'} (à confirmer){r.stagiaire ? ' · stagiaire' : ''}</span>
                          <span className="text-ink-3 tabular-nums" style={{ whiteSpace: 'nowrap' }}>{formatDateCourt(s.date_atelier)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {seancesRecherche.length > 0 && (
                <div className="mb-12">
                  <div className="text-xs font-semi mb-4" style={{ color: 'var(--warn)' }}>● Recherche interne à lancer ({seancesRecherche.length})</div>
                  <div className="flex flex-col gap-4">
                    {seancesRecherche.map((s) => {
                      const cle = cleSeance(s.atelier_id, s.date_atelier);
                      const r = getReso(cle);
                      const at = ateliersMap[s.atelier_id];
                      return (
                        <div key={cle} className="flex items-center gap-8 text-sm" style={{ background: 'var(--warn-dim)', borderRadius: 'var(--r2)', padding: '6px 10px' }}>
                          <span className="font-medium" style={{ flexShrink: 0 }}>{at?.nom ?? s.atelier_id}{at?.lieu && <span className="text-ink-3 font-normal"> · {at.lieu}</span>}</span>
                          <span className="text-ink-2" style={{ flex: 1, minWidth: 0 }}>{r.suggeresIds.length > 0 ? `${r.suggeresIds.length} suggestion(s)` : 'aucune suggestion cochée'}</span>
                          <span className="text-ink-3 tabular-nums" style={{ whiteSpace: 'nowrap' }}>{formatDateCourt(s.date_atelier)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {dejaDemandeesNonSelectionnees.length > 0 && (
                <div className="mb-12">
                  <div className="text-xs font-semi mb-4" style={{ color: 'var(--ink-3)' }}>
                    ● Déjà demandé·e·s ({dejaDemandeesNonSelectionnees.length})
                  </div>
                  <div className="flex flex-col gap-4">
                    {dejaDemandeesNonSelectionnees.map((d) => (
                      <div key={d.id} className="flex items-center gap-8 text-sm" style={{ background: 'var(--bg-inset)', borderRadius: 'var(--r2)', padding: '6px 10px' }}>
                        <span className="font-medium" style={{ flexShrink: 0 }}>{d.atelier_nom}{d.atelier_lieu && <span className="text-ink-3 font-normal"> · {d.atelier_lieu}</span>}</span>
                        <span style={{ flex: 1, minWidth: 0 }}><StatutBadge statut={d.statut} libelle={meta?.statuts[d.statut]} compact /></span>
                        <span className="text-ink-3 tabular-nums" style={{ whiteSpace: 'nowrap' }}>{formatDateCourt(d.date_atelier)}</span>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDemandeApercu(d)}>Voir</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(notesHorsAtelier || notes.trim()) && (
                <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8 }}>
                  {notesHorsAtelier && (
                    <div className="flex gap-8 text-xs mb-4">
                      <span className="text-ink-3" style={{ minWidth: 96, flexShrink: 0 }}>Hors atelier</span>
                      <span className="text-ink-1">{notesHorsAtelier}</span>
                    </div>
                  )}
                  {notes.trim() && (
                    <div className="flex gap-8 text-xs">
                      <span className="text-ink-3" style={{ minWidth: 96, flexShrink: 0 }}>Notes</span>
                      <span className="text-ink-1">{notes}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="card mt-12">
              <div className="card-title mb-8">Motif de l'absence</div>
              <div className="flex gap-16 flex-wrap text-sm">
                <label className="flex items-center gap-8">
                  <input type="radio" name="motif" checked={motif === 'vacances'} onChange={() => setMotif('vacances')} /> Vacances
                </label>
                <label className="flex items-center gap-8">
                  <input type="radio" name="motif" checked={motif === 'autre'} onChange={() => setMotif('autre')} /> Autre motif (formation, rendez-vous, maladie…)
                </label>
              </div>
              {motif === 'vacances' && soldeInfo?.verifie && (
                <div className={`text-xs mt-8 ${soldeInfo.hors_solde ? 'text-warn' : 'text-ink-3'}`}>
                  Solde de vacances dans Tipee : {formatHeures(soldeInfo.solde_heures)} · cette absence : {formatHeures(soldeInfo.heures_demandees)}
                  {soldeInfo.hors_solde && ' — le solde ne suffit pas.'}
                </div>
              )}
            </div>

            <CarteTipee tipeeUrl={tipeeUrl} confirme={tipeeConfirme} onConfirmer={setTipeeConfirme} nomAbsent={pourMoi ? null : (personneAbsente?.display_name ?? prenomAbsent)} />

            {avertSolde && (
              <div className="card mt-12" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
                <div className="card-title mb-8">Solde de vacances insuffisant</div>
                <p className="text-sm text-ink-1 mb-12">
                  {pourMoi ? 'Votre solde' : `Le solde ${dePrenom}`} de vacances dans Tipee ({formatHeures(avertSolde.solde_heures)}) ne
                  couvre pas cette absence ({formatHeures(avertSolde.heures_demandees)}). Voulez-vous quand même envoyer la
                  demande ? Les RH seront informé·e·s qu'elle dépasse le solde.
                </p>
                <div className="flex gap-8">
                  <button type="button" className="btn" onClick={() => setAvertSolde(null)} disabled={envoi}>Revenir</button>
                  <button type="button" className="btn btn-accent" onClick={() => creerDemandes(true)} disabled={envoi}>
                    Envoyer quand même
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex gap-8 mt-16">
          {etape !== 1 && <button type="button" className="btn" onClick={precedent} disabled={envoi}>Précédent</button>}
          <div className="flex-1" />
          {etape !== 3 ? (
            <button type="button" className="btn btn-accent" onClick={suivant}>Suivant</button>
          ) : (
            <div className="flex flex-col items-end gap-4">
              <button type="button" className="btn btn-accent" onClick={() => creerDemandes()} disabled={envoi || !tipeeConfirme || motif == null || avertSolde != null}>
                {envoi ? <span className="spinner spinner-sm" /> : 'Créer les demandes'}
              </button>
              {motif == null && <span className="text-xs text-ink-3">Indiquez d'abord le motif de l'absence.</span>}
              {motif != null && !tipeeConfirme && <span className="text-xs text-ink-3">Répondez d'abord au rappel Tipee ci-dessus.</span>}
            </div>
          )}
        </div>
      </div>

      {demandesCreees && (
        <ModalConfirmationCreation
          creees={demandesCreees}
          doublons={doublonsExistants ?? []}
          libelleStatut={(s) => meta?.statuts[s]}
          onApercu={(d) => setDemandeApercu(d)}
          onTableauDeBord={() => naviguer('tableau')}
          apercuOuvert={demandeApercu != null}
        />
      )}

      {demandeApercu && (
        <ModalApercuDemande
          demande={demandeApercu}
          libelleStatut={meta?.statuts[demandeApercu.statut]}
          onClose={() => setDemandeApercu(null)}
          onOuvrirFiche={() => naviguer('demandes', demandeApercu.id)}
        />
      )}
    </div>
  );
}

interface ModalConfirmationCreationProps {
  creees: Demande[];
  /** Séances demandées mais déjà couvertes par une demande active — jamais
   *  passées sous silence : le récapitulatif précédent annonçait un total
   *  (« N ateliers concernés ») qui doit rester cohérent avec ce que montre
   *  cette modale, même quand une partie existait déjà. */
  doublons: DoublonDemande[];
  libelleStatut: (statut: Demande['statut']) => string | undefined;
  // « Voir » ouvre un aperçu PAR-DESSUS (refermable), jamais une navigation
  // qui ferait quitter la confirmation et perdre l'accès aux autres demandes
  // (voir feedback-ux-first : ne jamais rediriger hors contexte).
  onApercu: (demande: Demande) => void;
  onTableauDeBord: () => void;
  /** Un aperçu est ouvert PAR-DESSUS cette confirmation : Échap doit alors le
   *  refermer LUI (géré par ModalApercuDemande), pas naviguer vers le Tableau
   *  de bord — sinon la même touche ferait deux choses et sortirait l'utilisateur. */
  apercuOuvert: boolean;
}

// Écran de fin de l'assistant : confirme ce qui a été créé plutôt que de
// naviguer silencieusement vers la première demande (perdait les autres de
// vue dès que plusieurs séances étaient demandées d'un coup). « Tableau de
// bord » est l'action par défaut (Échap/✕/clic hors modale).
function ModalConfirmationCreation({ creees, doublons, libelleStatut, onApercu, onTableauDeBord, apercuOuvert }: ModalConfirmationCreationProps) {
  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape' && !apercuOuvert) onTableauDeBord(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onTableauDeBord, apercuOuvert]);

  const total = creees.length + doublons.length;

  return (
    <div className="modal-overlay" onClick={onTableauDeBord}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {creees.length === 1 && doublons.length === 0 ? 'Demande enregistrée' : `${creees.length} demande${creees.length > 1 ? 's' : ''} enregistrée${creees.length > 1 ? 's' : ''}`}
          </h3>
          <button className="close-btn" onClick={onTableauDeBord} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          <div className="flex flex-col gap-8">
            {creees.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-8">
                <span className="text-sm">
                  <span className="text-ink-3 tabular-nums">{formatDateCourt(d.date_atelier)}</span> · {d.atelier_nom}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}
                </span>
                <div className="flex items-center gap-8">
                  <StatutBadge statut={d.statut} libelle={libelleStatut(d.statut)} compact />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => onApercu(d)}>Voir</button>
                </div>
              </div>
            ))}
          </div>

          {doublons.length > 0 && (
            <div className="mt-12" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <div className="text-xs text-ink-3 mb-8">
                {doublons.length > 1
                  ? `${doublons.length} séances étaient déjà demandées — rien à refaire :`
                  : 'Cette séance était déjà demandée — rien à refaire :'}
              </div>
              <div className="flex flex-col gap-8">
                {doublons.map((d) => 'acces' in d && d.acces === 'prise_en_charge' ? (
                  <div key={`${d.atelier_id}_${d.date_atelier}`} className="flex items-center justify-between gap-8">
                    <span className="text-sm text-ink-2">
                      <span className="text-ink-3 tabular-nums">{formatDateCourt(d.date_atelier)}</span> · {d.atelier_nom}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}
                    </span>
                    <span className="text-xs text-ink-3">Prise en charge par l'équipe gestion</span>
                  </div>
                ) : (
                  <div key={(d as Demande).id} className="flex items-center justify-between gap-8">
                    <span className="text-sm text-ink-2">
                      <span className="text-ink-3 tabular-nums">{formatDateCourt(d.date_atelier)}</span> · {d.atelier_nom}{d.atelier_lieu ? ` · ${d.atelier_lieu}` : ''}
                    </span>
                    <div className="flex items-center gap-8">
                      <StatutBadge statut={(d as Demande).statut} libelle={libelleStatut((d as Demande).statut)} compact />
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onApercu(d as Demande)}>Voir</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {total > 1 && (
            <div className="text-xs text-ink-3 mt-12">{total} séance{total > 1 ? 's' : ''} au total prise{total > 1 ? 's' : ''} en charge.</div>
          )}
          <div className="text-sm text-ink-3 mt-12">Retrouvez-les à tout moment dans le Tableau de bord.</div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm btn-accent" onClick={onTableauDeBord}>Tableau de bord</button>
        </div>
      </div>
    </div>
  );
}

interface ModalApercuDemandeProps {
  demande: Demande;
  libelleStatut?: string;
  onClose: () => void;
  onOuvrirFiche: () => void;
}

// Aperçu en lecture seule d'une demande déjà existante (détectée en doublon à
// l'étape 3) : vérifier son statut sans quitter l'assistant. « Ouvrir la
// fiche complète » reste disponible pour qui veut vraiment aller éditer.
function ModalApercuDemande({ demande: d, libelleStatut, onClose, onOuvrirFiche }: ModalApercuDemandeProps) {
  useEffect(() => {
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [onClose]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title"><PoleDot couleur={d.pole_couleur} /> {d.atelier_nom}</h3>
          <button className="close-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">
          <div className="flex items-center gap-8 mb-12">
            <StatutBadge statut={d.statut} libelle={libelleStatut} />
            <span className="text-ink-3 text-sm tabular-nums">{formatDateCourt(d.date_atelier)}</span>
          </div>
          <div className="flex flex-col gap-8 text-sm">
            {d.absent_nom && (
              <div className="flex gap-8"><span className="text-ink-3" style={{ minWidth: 110, flexShrink: 0 }}>Absent·e</span><span className="text-ink-1">{d.absent_nom}</span></div>
            )}
            {d.atelier_lieu && (
              <div className="flex gap-8"><span className="text-ink-3" style={{ minWidth: 110, flexShrink: 0 }}>Lieu</span><span className="text-ink-1">{d.atelier_lieu}</span></div>
            )}
            <div className="flex gap-8"><span className="text-ink-3" style={{ minWidth: 110, flexShrink: 0 }}>Remplaçant·e</span><span className="text-ink-1">{d.remplacant_nom || '—'}</span></div>
            {d.prochaine_echeance && (
              <div className="flex gap-8"><span className="text-ink-3" style={{ minWidth: 110, flexShrink: 0 }}>Prochaine échéance</span><span className="text-ink-1">{formatDateCourt(d.prochaine_echeance)}</span></div>
            )}
            {d.notes && (
              <div className="flex gap-8"><span className="text-ink-3" style={{ minWidth: 110, flexShrink: 0 }}>Notes</span><span className="text-ink-1">{d.notes}</span></div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-sm btn-ghost" onClick={onOuvrirFiche}>Ouvrir la fiche complète</button>
          <button type="button" className="btn btn-sm btn-accent" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

interface CarteTipeeProps {
  tipeeUrl: string;
  confirme: boolean;
  onConfirmer: (v: boolean) => void;
  /** Nom de la personne absente quand ce n'est pas la personne connectée (gestion) ; null = « vous ». */
  nomAbsent?: string | null;
}

// Rappel Tipee actionnable et désormais bloquant : « Créer les demandes »
// reste grisé tant que ce bloc n'a pas de réponse. Les deux options sont
// proposées d'emblée, côte à côte — pas de séquence forcée : si c'est déjà
// fait (avant même d'ouvrir l'assistant), on confirme directement ; sinon on
// ouvre Tipee puis on revient confirmer. Le libellé de confirmation reflète
// juste ce qui a été fait dans CETTE visite (« déjà fait » vs « fait à
// l'instant ») — un simple repère contextuel, il ne conditionne rien.
// Pas d'échappatoire « plus tard ».
function formatHeures(h?: number): string {
  return `${(h ?? 0).toLocaleString('fr-CH', { maximumFractionDigits: 1 })} h`;
}

function CarteTipee({ tipeeUrl, confirme, onConfirmer, nomAbsent }: CarteTipeeProps) {
  const [ouvert, setOuvert] = useState(false);

  if (confirme) {
    return (
      <div className="card flex items-center justify-between gap-12" style={{ background: 'var(--ok-dim)', borderColor: 'var(--ok)' }}>
        <span className="text-ink-1">✓ {nomAbsent ? 'Demande de congé Tipee vérifiée.' : 'Demande de congé Tipee confirmée.'}</span>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => onConfirmer(false)}>Annuler</button>
      </div>
    );
  }
  return (
    <div className="card" style={{ background: 'var(--warn-dim)', borderColor: 'var(--warn)' }}>
      <div className="text-ink-1 mb-8">{nomAbsent ? `Vérifiez ${queNom(nomAbsent)} a bien fait sa demande de congé dans Tipee.` : BANNIERE_TIPEE}</div>
      <div className="flex gap-8 flex-wrap">
        {tipeeUrl && (
          <a className="btn btn-sm" href={tipeeUrl} target="_blank" rel="noreferrer" onClick={() => setOuvert(true)}>
            Ouvrir Tipee <ExternalLink className="icon-sm" />
          </a>
        )}
        <button type="button" className="btn btn-sm btn-accent" onClick={() => onConfirmer(true)}>
          {nomAbsent ? "C'est vérifié" : (ouvert ? "C'est fait" : "C'est déjà fait")}
        </button>
      </div>
    </div>
  );
}
